import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router";
import { LuFileText, LuLock, LuTarget, LuActivity, LuSparkles } from "react-icons/lu";
import { Images } from "../controller/Images";
import { GlobalStateContext } from "../controller/GlobalState";
import { emailRegex, isValidPhone, blogPosts, workflowsteps, chooseus } from "../controller/global";
import { submitLead } from "../controller/leads";

const TRUST_LOGOS = [
   // { name: "Aptor", src: Images.aptor_logo }, { name: "BoxERP", src: Images.boxErp }, { name: "GE", src: Images.ge_logo },
  { name: "Jennison", src: Images.Jennison_Dark_logo }, { name: "Regent", src: Images.regent_logo },
  { name: "ThreeLockBox", src: Images.threelockbox_logo }, { name: "Stokkd", src: Images.stokkd_logo }, { name: "Ujala Enterprises", src: Images.UjalaEnterprises_logo },
];

// No stock photography — every case study gets a real icon instead, matching
// the rest of the site (see /services).
const CASE_ICONS = {
  ezrater: LuFileText,
  "three-lock-box": LuLock,
  "goal-envision": LuTarget,
  "gym-chain-integration": LuActivity,
  "ai-style-recommendation": LuSparkles,
};

const PROCESS_STEPS = workflowsteps.map((w) => ({ n: w.number, t: w.title, d: w.description }));

const FACTS = [
  { a: "5", b: "Industries served" },
  { a: "7", b: "Stage delivery process" },
  { a: "MSME", b: "Registered IT company" },
  { a: "Delhi NCR", b: "Headquarters" },
];

const FAQS = [
  { q: "Can we see more detail on a project similar to ours?", a: "Yes — reach out and we'll walk through the closest relevant case study in more depth during a call." },
  { q: "Do you sign NDAs before sharing confidential project details?", a: "Yes — we're happy to sign an NDA before discussing anything beyond what's public here." },
  { q: "What engagement models do you offer?", a: "We work Fixed-Scope, Dedicated Team, or MVP Sprint — the same process and team stay accountable from kickoff to launch regardless of which model fits your project." },
  { q: "Where is your team based?", a: "We're an MSME-registered IT company headquartered in Delhi NCR, following the same 7-stage delivery process across every engagement." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag className={className} custom={i} variants={fadeUp} initial={reduceMotion ? false : "hidden"} whileInView="show" viewport={{ once: true, amount: 0.2 }} {...rest}>
      {children}
    </MotionTag>
  );
}

const CaseStudies = () => {
  const navigate = useNavigate();
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();

  const featured = blogPosts[0];
  const rest = blogPosts.slice(1);

  useEffect(() => {
    document.title = "Client Case Studies: AI, Insurtech & IoT | InQmise";
    const description = document.createElement("meta");
    description.name = "description";
    description.content = "Five client engagements in detail: insurance quote aggregation, construction escrow, gym hardware integration, retail AI and strategy SaaS.";
    document.head.appendChild(description);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://inqmisetechnologies.com/case-studies");

    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Case Studies",
      "url": "https://inqmisetechnologies.com/case-studies",
      "hasPart": blogPosts.map((post) => ({
        "@type": "CreativeWork",
        "name": post.title,
        "url": `https://inqmisetechnologies.com/case-studies/${post.slug}`,
      })),
    });
    document.head.appendChild(schemaScript);

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({ "@type": "Question", "name": item.q, "acceptedAnswer": { "@type": "Answer", "text": item.a } })),
    });
    document.head.appendChild(faqScript);
  }, []);

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname": return value.trim() ? "" : "Full name is required.";
      case "phone":
        if (!value.trim()) return "";
        return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject": return value.trim() ? "" : "Subject is required.";
      case "description": return value.trim() ? "" : "Tell us a little about your project.";
      default: return "";
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const newErrors = {
      fullname: validateContactField("fullname", formData.fullname),
      phone: validateContactField("phone", formData.phone),
      email: validateContactField("email", formData.email),
      subject: validateContactField("subject", formData.subject),
      description: validateContactField("description", formData.description),
    };
    if (Object.values(newErrors).some(Boolean)) {
      setContactErrors(newErrors);
      return;
    }
    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => {
        setContactStatus("success");
        setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" });
        setContactErrors({});
      })
      .catch(() => setContactStatus("error"));
  };

  const openContactModal = () => setIsOpen(true);
  const FeaturedIcon = CASE_ICONS[featured.slug] || LuFileText;

  return (
    <main className="editorial-ai pt-20">
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <p className="ea-eyebrow">Case studies · real client work</p>
          <h1>Real projects, <em>real outcomes</em>.</h1>
          <p className="ea-lede">Five real engagements — insurance tech, construction escrow, SaaS strategy, gym-chain IoT, and AI-powered retail — each solved with the same disciplined process.</p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to start your project">Start your project ↗</button>
            <a className="ea-text-link" href="#case-studies">Explore case studies</a>
          </div>
        </Reveal>

        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / CASE STUDIES <b>LIVE</b></div>
          <h2><FeaturedIcon aria-hidden="true" style={{ verticalAlign: "-4px", marginRight: 8 }} />{featured.title}</h2>
          <div className="ea-query"><small>INDUSTRIES</small>InsurTech, construction escrow, SaaS, IoT/fitness, and AI retail.</div>
          <div className="ea-query"><small>APPROACH</small>The same 7-stage delivery process, applied to every engagement.</div>
          <div className="ea-flow"><b>Scope</b><i>→</i><b>Build</b><i>→</i><b>Deliver</b></div>
        </Reveal>
      </header>

      <div className="ea-wrap ea-logo-row">
        <span>TRUSTED BY GROWING BUSINESSES</span>
        <div className="ea-logo-track">
          <div className="ea-logo-track-inner">
            {[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => (
              <img key={`${logo.name}-${i}`} src={logo.src} alt={logo.name} loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* FEATURED CASE STUDY */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Featured case study</div>
            <h2>{featured.category}</h2>
          </div>
          <p>A closer look at the work we're proudest of.</p>
        </Reveal>
        <Reveal as="div" className="ea-case">
          <div>
            <div className="ea-eyebrow">{featured.category}</div>
            <h3>{featured.title}</h3>
            <p>{featured.description}</p>
            <button className="ea-text-link" onClick={() => navigate(`/case-studies/${featured.slug}`)}>Read the full case study →</button>
          </div>
          <div className="ea-results">
            {rest.slice(0, 4).map((post) => (
              <div key={post.slug}><b>{post.category.split(" / ")[0].split(" ")[0]}</b><span>{post.title.split(":")[0]}</span></div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* FULL GRID */}
      <section className="ea-services" id="case-studies">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">All case studies</div>
              <h2>Five engagements.<br />One accountable team.</h2>
            </div>
            <p>Each case study has its own dedicated page with real detail on the problem, build, and outcome.</p>
          </Reveal>
          <div className="bento-cs-grid">
            {blogPosts.map((post, i) => {
              const Icon = CASE_ICONS[post.slug] || LuFileText;
              return (
                <Reveal
                  as="a"
                  i={(i % 4) + 1}
                  reduceMotion={reduceMotion}
                  key={post.slug}
                  className={`bento-cs-card bento-cs-card--${i}`}
                  href={`/case-studies/${post.slug}`}
                  onClick={(e) => { e.preventDefault(); navigate(`/case-studies/${post.slug}`); }}
                >
                  <img className="bento-cs-img" src={post.image} alt={post.title} loading="lazy" />
                  <div className="bento-cs-scrim" aria-hidden="true" />
                  <div className="bento-cs-icon"><Icon aria-hidden="true" /></div>
                  <div className="bento-cs-body">
                    <span className="bento-cs-tag">{post.category}</span>
                    <h3>{post.title}</h3>
                    {i === 0 && <p>{post.description}</p>}
                    <span className="bento-cs-link">Read the full case study <i aria-hidden="true">→</i></span>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Our process</div>
            <h2>The same process<br />behind every case study.</h2>
          </div>
          <p>The same disciplined process behind every service we offer.</p>
        </Reveal>
        <div className="aa-process-line">
          {PROCESS_STEPS.map((step, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="aa-process-row" key={step.n}>
              <div className="aa-process-copy"><h3>{step.t}</h3><p>{step.d}</p></div>
              <div className="aa-process-node">{step.n}</div>
              <div />
            </Reveal>
          ))}
        </div>
      </section>

      {/* FACTS */}
      <section className="ea-models">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">By the numbers</div>
              <h2>Real facts,<br />not vanity metrics.</h2>
            </div>
            <p>The facts behind the team delivering every one of these projects.</p>
          </Reveal>
          <div className="ea-metrics">
            {FACTS.map((m, i) => (
              <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-metric" key={m.a}>
                <strong>{m.a}</strong><span>{m.b}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Why choose us</div>
            <h2>What sets our<br />team apart.</h2>
          </div>
          <p>The same standards behind every project on this page.</p>
        </Reveal>
        <div className="ea-cards">
          {chooseus.map((item, i) => (
            <Reveal as="article" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ea-card" key={item.title}>
              <div className="ea-icon"><item.icon aria-hidden="true" /></div>
              <div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section">
          <Reveal as="div" className="ea-section-head">
            <div className="ea-eyebrow">Common questions</div>
            <h2>Questions about our case studies</h2>
            <p>Short answers to the questions we hear most.</p>
          </Reveal>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i;
            const panelId = `case-faq-panel-${i}`;
            const buttonId = `case-faq-button-${i}`;
            return (
              <div className="ea-faq-row" key={item.q}>
                <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                  <button id={buttonId} aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpenFaq(isOpen ? -1 : i)}>
                    {item.q}<span>{isOpen ? "−" : "+"}</span>
                  </button>
                </h3>
                <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="ea-cta" aria-labelledby="case-contact-heading">
        <div className="ea-wrap ea-cta-grid">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <div className="ea-eyebrow">A practical first step</div>
            <h2 id="case-contact-heading">Ready to build your next success story?</h2>
            <p>Tell us what you're building and we'll help you figure out the right approach.</p>
          </Reveal>

          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ea-contact-card">
            <span>READY WHEN YOU ARE</span>
            <h3>Start your project.</h3>
            <p>Tell us what you're building and we'll get back to you within one business day.</p>

            <form onSubmit={handleContactSubmit} aria-label="Case studies page project inquiry form" className="ea-form" noValidate>
              {contactStatus === "success" && (
                <p role="status" aria-live="polite" className="ea-form-msg success">
                  Thanks — we've received your details and will be in touch shortly.
                </p>
              )}
              {contactStatus === "error" && (
                <p role="alert" aria-live="assertive" className="ea-form-msg error">
                  Something went wrong submitting your request. Please try again.
                </p>
              )}

              <div className="ea-field">
                <label htmlFor="case-fullname">Full Name</label>
                <input
                  id="case-fullname" name="fullname" type="text" value={formData.fullname} onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.fullname)} aria-describedby={contactErrors.fullname ? "case-fullname-error" : undefined}
                />
                {contactErrors.fullname && <p id="case-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>

              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="case-phone">Phone Number (optional)</label>
                  <input
                    id="case-phone" name="phone" type="tel" value={formData.phone} onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.phone)} aria-describedby={contactErrors.phone ? "case-phone-error" : undefined}
                  />
                  {contactErrors.phone && <p id="case-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="case-email">Email</label>
                  <input
                    id="case-email" name="email" type="email" value={formData.email} onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.email)} aria-describedby={contactErrors.email ? "case-email-error" : undefined}
                  />
                  {contactErrors.email && <p id="case-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>

              <div className="ea-field">
                <label htmlFor="case-subject">Subject</label>
                <input
                  id="case-subject" name="subject" type="text" placeholder="e.g., A project like one of your case studies"
                  value={formData.subject} onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.subject)} aria-describedby={contactErrors.subject ? "case-subject-error" : undefined}
                />
                {contactErrors.subject && <p id="case-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>

              <div className="ea-field">
                <label htmlFor="case-description">Project Details</label>
                <textarea
                  id="case-description" name="description" rows={3} value={formData.description} onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.description)} aria-describedby={contactErrors.description ? "case-description-error" : undefined}
                />
                {contactErrors.description && <p id="case-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>

              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">
                {contactStatus === "submitting" ? "Submitting…" : "Start a conversation ↗"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default CaseStudies;
